import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { userAuthorization } from 'src/utils/auth';
import { useTranslation } from 'react-i18next';
import ColumnFilterText from './ColumnFilterText';
import ColumnFilterList from './ColumnFilterList';
import ColumnFilterDate from './ColumnFilterDate';
import ColumnFilterCompany from './ColumnFilterCompany';
import ColumnFiltersMorePopup from './ColumnFiltersMorePopup';
import s from './ColumnFilters.styles';

const ColumnFilters = ({
  columns,
  refetch,
  filters,
  setFilters: setFiltersReal,
  withDeleted,
  setWithDeleted,
  hideDeletedButton = false,
  dateLinks,
}) => {
  const { t } = useTranslation();
  const [moreActive, setMoreActive] = useState(false);

  const onReload = newFilters => {
    const columnFilters = newFilters.map(({ column, field, value }) => ({
      field,
      value: (column.filterType === 'company') ? JSON.stringify(value.ids) : JSON.stringify(value),
    }));
    refetch({ columnFilters });
  };

  const setFilters = newFilters => {
    setFiltersReal(newFilters);
    onReload(newFilters);
  };

  const onFilterAdd = column => {
    setFilters([
      ...filters,
      {
        field: column.filter || column.name,
        value: [],
        column,
      },
    ]);
    setTimeout(() => setMoreActive(false), 100);
  };

  const onFilterValueChange = (index, value) => {
    const newFilters = [...filters];
    newFilters[index].value = value;
    setFilters(newFilters);
  };

  const onFilterRemove = index => {
    const newFilters = [...filters];
    newFilters.splice(index, 1);
    setFilters(newFilters);
  };

  const moreColumns = columns.filter(({ filter, name }) => {
    if (filter === null || (!filter && !name)) {
      return false;
    }

    return !filters.find(c => c.field === (filter || name));
  });

  const auth = userAuthorization();
  if (columns.length === 0) {
    return null;
  }

  return (
    <s.FiltersWrapper>
      <s.FiltersLabel>{t('FILTER_BY_COLUMNS')}</s.FiltersLabel>

      {filters.map(({ field, value, column }, index) => {
        const mapping = {
          text: ColumnFilterText,
          list: ColumnFilterList,
          date: ColumnFilterDate,
          company: ColumnFilterCompany,
        };
        const ColumnFilter = mapping[column.filterType];
        const rest = columns.find(c => (c.filter || c.name) === field);

        return (
          (!auth.isFactoryUser || auth.isAdmin ) && (
            <ColumnFilter
              {...rest}
              key={`${index}:${JSON.stringify(value)}`}
              column={{
                ...column,
                label: rest?.label || '...',
              }}
              dateLinks={dateLinks}
              onChange={newValue => onFilterValueChange(index, newValue)}
              onRemove={() => onFilterRemove(index)}
              setMoreActive={setMoreActive}
              value={value}
            />
          )
        );
      })}

      {moreColumns.length > 0 && (
        <s.MoreButton onClick={() => setMoreActive(true)}>
          + {t('MORE')}
          {moreActive && (
            <ColumnFiltersMorePopup
              columns={moreColumns}
              onClose={() => setMoreActive(false)}
              onSubmit={onFilterAdd}
            />
          )}
        </s.MoreButton>
      )}

      {!hideDeletedButton && (
        <s.DeletedBlock
          onClick={() => setWithDeleted(!withDeleted)}
          selected={withDeleted}
        >
          {withDeleted ? t('HIDE_DELETED_ROWS') : t('SHOW_DELETED_ROWS')}
          <s.DeletedIconWrapper>
            <s.DeletedIcon name={withDeleted ? 'eye' : 'eye-cross'} />
          </s.DeletedIconWrapper>
        </s.DeletedBlock>
      )}
    </s.FiltersWrapper>
  );
};

ColumnFilters.propTypes = {
  columns: PropTypes.array,
  filters: PropTypes.array,
  setFilters: PropTypes.func,
  refetch: PropTypes.func,
  withDeleted: PropTypes.bool,
  setWithDeleted: PropTypes.func,
  hideDeletedButton: PropTypes.bool,
  dateLinks: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, onClick: PropTypes.func }),
  ),
};

export default React.memo(ColumnFilters);
